/** Cuerpo máximo (8 dígitos) más el dígito verificador. */
const MAX_RUT_LENGTH = 9;

/**
 * Deja solo dígitos y K/k, en mayúscula, cortado al largo máximo de un RUT.
 * Punto de partida para validar o formatear.
 *
 * El corte importa: `RutFormatDirective` escribe con `renderer.setProperty`, y
 * el `maxlength` del input no aplica a los cambios programáticos. Sin esto, un
 * RUT pegado desde el portapapeles se formatea sin tope.
 */
export function cleanRut(value: string | null | undefined): string {
  return (value ?? '').replace(/[^0-9kK]/g, '').toUpperCase().slice(0, MAX_RUT_LENGTH);
}

/** Dígito verificador módulo 11 sobre el cuerpo del RUT (sin el propio DV). */
export function computeVerifierDigit(body: string): string {
  let sum = 0;
  let multiplier = 2;

  for (let i = body.length - 1; i >= 0; i--) {
    sum += Number(body[i]) * multiplier;
    multiplier = multiplier === 7 ? 2 : multiplier + 1;
  }

  const remainder = 11 - (sum % 11);

  if (remainder === 11) {
    return '0';
  }

  if (remainder === 10) {
    return 'K';
  }

  return String(remainder);
}

/** Un RUT es válido cuando tiene 7-8 dígitos de cuerpo y su DV coincide con el calculado. */
export function isValidRut(value: string | null | undefined): boolean {
  const clean = cleanRut(value);

  if (!/^\d{7,8}[0-9K]$/.test(clean)) {
    return false;
  }

  const body = clean.slice(0, -1);
  const dv = clean.slice(-1);

  return computeVerifierDigit(body) === dv;
}

/** Formatea a `12.345.678-9`. Formatea parcialmente mientras el usuario todavía está escribiendo. */
export function formatRut(value: string | null | undefined): string {
  const clean = cleanRut(value);

  if (clean.length <= 1) {
    return clean;
  }

  const body = clean.slice(0, -1);
  const dv = clean.slice(-1);
  const formattedBody = body.replace(/\B(?=(\d{3})+(?!\d))/g, '.');

  return `${formattedBody}-${dv}`;
}

/** Formato que espera el backend: `12345678-9`, sin puntos de miles. */
export function toDashedRut(value: string | null | undefined): string {
  const clean = cleanRut(value);

  if (clean.length <= 1) {
    return clean;
  }

  return `${clean.slice(0, -1)}-${clean.slice(-1)}`;
}
